import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { LookupsService, SvgIconsComponent } from '@zayan-erp/shared';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { InputNumberModule } from '@openng/optimus-ui/inputnumber';
import { InputOtpModule } from '@openng/optimus-ui/inputotp';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { SchedulerService } from '@zayan-erp/scheduler';
import { WarrantyService } from '@zayan-erp/warranty';

@Component({
  selector: 'app-schedule-vpo',
  standalone: true,
  imports: [
    CommonModule,
    DatePickerModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    NgSelectModule,
    InputOtpModule,
    SvgIconsComponent
  ],
  templateUrl: './schedule-vpo.component.html',
  styleUrl: './schedule-vpo.component.scss'
})
export class ScheduleVpoComponent implements OnInit {
  @Input() scheduleLotTaskId!: string;
  showVpoDialog = signal(false);
  #lookups = inject(LookupsService);
  #warranty = inject(WarrantyService);
  #scheduler = inject(SchedulerService);
  #modalService = inject(NgbModal);

  @ViewChild('inputO') inputO!: ElementRef;
  @ViewChild('inputP') inputP!: ElementRef;
  @ViewChild('inputL') inputL!: ElementRef;
  @ViewChild('inputM') inputM!: ElementRef;
  @ViewChild('inputN') inputN!: ElementRef;
  aloc: string = '';
  isUpdate: boolean = false;

  form = new FormGroup({
    allocation: new FormControl<string | null>(null, [Validators.required]),
    scheduleLotTaskId: new FormControl<string | null>(null, [Validators.required]),
    supplierId: new FormControl<string | null>(null, [Validators.required]),
    poNo: new FormControl<string | null>(null, [Validators.required]),
    requiredDate: new FormControl<Date | null>(null, [Validators.required]),
    craftId: new FormControl<string | null>(null),
    craftType: new FormControl<string | null>(null),
    varianceReasonId: new FormControl<string | null>(null, [Validators.required]),
    backOfficeNotes: new FormControl<string | null>(null, [Validators.required]),
    quantity: new FormControl<number | null>(null, [Validators.required]),
    unitPrice: new FormControl<number | null>(null, [Validators.required]),
    uofmId: new FormControl(null, [Validators.required]),
    measureNotes: new FormControl(null, [Validators.required])
  });

  varianceReasons = toSignal(this.#lookups.readVarianceReasons(), { initialValue: [] });
  uofm = toSignal(this.#lookups.readUofm(), { initialValue: [] });
  traders = toSignal(this.#lookups.readSuppliers(), { initialValue: [] });
  crafts = toSignal(this.#warranty.readWarrantyCrafts(), { initialValue: [] });
  craftTypes = [
    { label: 'Subcontract', value: 'Subcontract' },
    { label: 'Parts', value: 'Parts' },
    { label: 'Bid Rate', value: 'bidRate' }
  ];

  ngOnInit(): void {
    this.form.get('scheduleLotTaskId')?.patchValue(this.scheduleLotTaskId);
    this.getVpoByScheduleLot();
  }
  onAllocChange(event: any, type: string) {
    const char = event.target.value;
    const indexMap = { O: 0, P: 1, L: 2, M: 3, N: 4 };
    const index = indexMap[type as keyof typeof indexMap];

    // Convert current string to array to allow index assignment
    let allocArray = this.aloc.split('');

    // Ensure the array has 5 elements
    while (allocArray.length < 5) {
      allocArray.push('');
    }

    allocArray[index] = char || '';

    this.aloc = allocArray.join('');

    // Patch updated value into form
    this.form.get('allocation')?.patchValue(this.aloc);

    // Focus next field
    switch (type) {
      case 'O':
        this.inputP.nativeElement.focus();
        break;
      case 'P':
        this.inputL.nativeElement.focus();
        break;
      case 'L':
        this.inputM.nativeElement.focus();
        break;
      case 'M':
        this.inputN.nativeElement.focus();
        break;
      case 'N':
        this.form.get('allocation')?.patchValue(this.aloc);
        break;
    }
  }

  getVpoByScheduleLot() {
    this.#scheduler.readVpoByScheduleLotTaskId(this.scheduleLotTaskId).subscribe(res => {
      if (res !== null) {
        // Convert the date string to a Date object if it exists
        if (res.requiredDate) {
          res.requiredDate = new Date(res.requiredDate);
        }
        this.form.patchValue(res);
        // Map allocation data to individual input boxes
        if (res.allocation) {
          this.mapAllocationToInputs(res.allocation);
        }
        this.isUpdate = true;
      }
    });
  }

  private mapAllocationToInputs(allocation: string) {
    // Ensure allocation is 5 characters long
    const paddedAllocation = allocation.padEnd(5, '');

    // Update the aloc property
    this.aloc = paddedAllocation;

    // Set values for each input box
    if (this.inputO) this.inputO.nativeElement.value = paddedAllocation[0] || '';
    if (this.inputP) this.inputP.nativeElement.value = paddedAllocation[1] || '';
    if (this.inputL) this.inputL.nativeElement.value = paddedAllocation[2] || '';
    if (this.inputM) this.inputM.nativeElement.value = paddedAllocation[3] || '';
    if (this.inputN) this.inputN.nativeElement.value = paddedAllocation[4] || '';
  }

  createVpo() {
    if (this.form.valid) {
      this.#scheduler.createVpo(this.form.value).subscribe(res => {
        this.dismissModal();
      });
    }
  }

  updateVpo() {
    if (this.form.valid) {
      this.#scheduler.updateVpo(this.form.value).subscribe(res => {
        this.dismissModal();
      });
    }
  }

  // hasFormValues(): boolean {
  //   const formValues = { ...this.form.value };
  //   delete formValues.scheduleLotTaskId;
  //   return Object.values(formValues).some(value => value !== null && value !== '');
  // }

  dismissModal() {
    this.#modalService.dismissAll();
  }
  customSearchFn(term: string, item: any): boolean {
    if (!term) return true;
    term = term.toLowerCase();

    // For traders (suppliers)
    if (item.name) {
      return item.name.toLowerCase().includes(term);
    }

    // For crafts
    if (item.craftCode && item.craftDescription) {
      return item.craftCode.toLowerCase().includes(term) || item.craftDescription.toLowerCase().includes(term);
    }

    // For variance reasons and UOFM
    if (item.code && item.description) {
      return item.code.toLowerCase().includes(term) || item.description.toLowerCase().includes(term);
    }

    // For craft types
    if (item.label) {
      return item.label.toLowerCase().includes(term);
    }

    return false;
  }
}
